<template>
  <div class="car-content car-main-tab-content car-reduced-width">
    <back-btn v-if="isServiceRequest" />
    <spinner v-if="loading" class="car-spinner car-main-content-spinner" />

    <div class="car-booking car-offer" data-js="booking-page" v-else>
      <offer-header :valid-offer="validOffer" />
      <div class="car-offer-details" v-if="validOffer">
        <div class="car-selected-services">
          <span class="car-label">
            {{$t('offer.serviceLabel')}}
          </span>
          <offer-service-selection
            :setLoading="updateLoading"
            :sections="selectedServices"
            :payloadkey="selectedOffer.offerKey"
            dispatch-path="offer/updateOffer"
            :all-sections="selectedOffer.actualSections.sections"/>
        </div>
        <div class="car-selected-additional-services" v-if="additionalServices.length">
          <div class="car-items">
            <div class="car-item">
              <accordion :label="$t('quote.additionalService')" :expand="isAdditionalServiceSelected()">
                <div class="car-additional-services">
                  <div class="car-additional-service-list">
                    <offer-service-selection
                      :setLoading="updateLoading"
                      :sections="additionalServices"
                      :payloadkey="selectedOffer.offerKey"
                      dispatch-path="offer/updateOffer"
                      :all-sections="selectedOffer.actualSections.sections"
                      :hideSectionLabel=true
                    ></offer-service-selection>
                  </div>
                </div>
              </accordion>
            </div>
          </div>
        </div>
        <div class="car-offer-price">
          <div class="car-items">
            <div class="car-item">
              <span class="car-label">{{$t('offer.price', {tax: selectedOffer.salesTax})}}</span>
              <span class="car-price">{{selectedOffer.actualGrossRate | currency}}</span>
            </div>
          </div>
        </div>
        <div class="car-buttons">
          <button
            class="car-btn car-btn-primary"
            @click="gotoBooking()"
          >{{$t('offer.button.book')}}
          </button>
          <span class="car-link" data-js="conversation-link" @click="gotoMessage()">{{$t('offer.button.sendMessage')}}</span>
        </div>
      </div>
      <div v-else>
        <div class="car-link" data-js="conversation-link" @click="gotoMessage()">{{$t('offer.button.sendMessage')}}</div>
      </div>
      <div id="booking-start"></div>
      <div class="car-content-introduction" v-if="validOffer">
        <span class="car-headline">{{$t('offer.appointment.title')}}</span>
        <p>{{$t('offer.appointment.description')}}</p>
      </div>

      <div class="car-appointment" v-if="validOffer">
        <span class="car-subheadline">{{$t('offer.selectTimeSlot')}}</span>

        <datepicker-container @selectDate="selectDate"
                              :selectedDate="selectedDate"
                              :available-dates="timeslots" />
        <div v-if="!$v.selectedTimeslot.required && $v.selectedTimeslot.$error" class="car-error-message">{{$t('offer.errors.date')}}</div>

        <v-select
          v-if="availableTimeSlotsForDate.length > 5"
          class="car-v-select"
          name="appointment"
          :value="selectedTimeslot"
          :options="availableTimeSlotsForDate"
          :getOptionLabel="timeslotLabel"
          @input="selectTimeslot"
          :placeholder="$t('offer.appointment.appointmentLabel')" />
        <time-slot
          v-else
          v-for="timeslot in availableTimeSlotsForDate"
          :key="timeslot.timeslotId"
          :timeslot="timeslot"
          @click="selectTimeslot(timeslot)"
          :class="{'car-selected-appointment': timeslot === selectedTimeslot }" />
      </div>

      <div class="car-booking-form">
        <span class="car-subheadline">
          {{$t('customer.title')}}
        </span>
        <customer :form="form" :v="$v" v-if="validOffer" />
        <agreements :form="form" :v="$v" v-if="validOffer" class="car-form-item" />

        <div class="car-form-error-message" v-if="$v.form.$error">{{ $t('errors.unknown') }}</div>
        <div class="car-form-buttons car-buttons" v-if="validOffer">
          <primary-btn
            @click="createBooking"
            class="car-btn-primary"
            :loading="offerLoading"
            id="booking">
            {{$t('offer.button.booking')}}
          </primary-btn>
          <div class="car-form-required">* {{ $t('catalogSelection.requiredNote') }}</div>
        </div>
      </div>

      <div class="car-info-box car-selection-overview">
        <ul class="car-notes">
          <li>{{$t('offer.note1')}}</li>
          <li>{{$t('offer.note2')}}</li>
          <li>{{$t('offer.note3')}}</li>
        </ul>
      </div>

      <div class="car-accordion-container">
        <accordion class="car-accordion-price" :logo="true" data-js="booking_calculation" :label="$t('accordion.price')">
          <offer-services-review
            data-js="detail__offer--review"
            :bookingServices="selectedOffer.bookingServices"
            :valuatedVehicle="offersearch.vehicleConfig"
            :selectedOffer="selectedOffer"
            :selectedServices="offersearch.services"
            :actualNetRate="selectedOffer.actualNetRate"
            :actualGrossRate="selectedOffer.actualGrossRate"
            :regularGrossRate="selectedOffer.regularGrossRate"
            :maintenancePositions="maintenancePositions"
            :all-sections="selectedOffer.actualSections.sections"/>
        </accordion>
        <accordion class="car-accordion-vehicle" :label="$t('accordion.vehicle')">
          <vehicle-details :vehicle="getVehicle()"></vehicle-details>
        </accordion>
        <accordion class="car-accordion-workshop" :label="$t('accordion.workshop')" data-js="workshop_details">
          <workshop-details :location-id="selectedOffer.location.id"/>
        </accordion>
        <accordion class="car-accordion-user-ratings" :label="$t('accordion.ratings')" v-if="comments && comments.length">
          <workshop-review :location-id="selectedOffer.location.id"></workshop-review>
        </accordion>
      </div>
      <div class="car-buttons">
        <button
          class="car-btn car-btn-primary"
          @click="gotoBooking()"
          v-if="validOffer"
        >{{$t('offer.button.book')}}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { filter, each, merge, get } from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import Spinner from 'vue-simple-spinner';

import bookingApiService from '@/services/bookingApiService';
import bookingSectionService from '@/services/bookingSectionService';

import { capacityId, locationId as contextLocationId } from '@/util/smp_config';

import Accordion from '@/components/Accordion';
import OfferServicesReview from '@/components/service-request/OfferServicesReview';
import VehicleDetails from '@/components/service-request/VehicleDetails';
import WorkshopDetails from '@/components/service-request/WorkshopDetails';
import WorkshopReview from '@/components/service-request/WorkshopReview';
import Customer from '@/components/service-request/Customer';
import OfferServiceSelection from '@/components/service-request/OfferServiceSelection';
import Agreements from '@/components/service-request/Agreements';
import BackBtn from '@/components/BackBtn';
import PrimaryBtn from '@/components/PrimaryBtn';
import DatepickerContainer from '@/components/Datepicker';
import TimeSlot from '@/components/TimeSlot';
import OfferHeader from '@/components/service-request/OfferHeader';
import capacityApiService from '@/services/capacityApiService';

const { required, email } = require('vuelidate/lib/validators');

const validAgreements = (value) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true
  }
  return value;
};
export default {
  name: 'Offer',
  data: function () {
    return {
      attrs: {
        highlight: {
          backgroundColor: '#EE3727'
        }
      },
      selectedDate: null,
      selectedTimeslot: null,
      form: {
        agreeTo: false,
        mileage: this.getInitMileage()
      },
      requestError: false,
      loading: false,
      offerLoading: false,
      timeslots: [],
      maintenancePositions: [],
      comments: [],
      display: {
        extraComments: false
      }
    }
  },
  validations: {
    selectedTimeslot: {
      required
    },
    form: {
      email: {
        required,
        email
      },
      firstName: {
        required
      },
      lastName: {
        required
      },
      phone: {
        required
      },
      agreeTo: {
        validAgreements
      }
    }
  },
  methods: {
    getInitMileage() {
      return this.$store.getters['wizardData/mileage'] || (
        this.$store.getters['vehicleIdentification/stateObj'] &&
        this.$store.getters['vehicleIdentification/stateObj'].mileage.value
      );
    },
    getVehicle() {
      let vehicleConfig = this.offersearch.vehicleConfig;
      vehicleConfig.selectedEquipments = filter(vehicleConfig.vehicleTypeEquipmentList, 'selected');
      return vehicleConfig;
    },
    selectDate(r) {
      this.selectedDate = r;

      if (!this.canUseCapacityAppointments) {
        this.selectedTimeslot = this.timeslots.find(item => moment(item.dateFrom.time).format('YYYY-MM-DD') === moment(r).format('YYYY-MM-DD'));
      }
    },
    selectTimeslot(timeslot) {
      this.selectedTimeslot = timeslot;
    },
    timeslotLabel(timeslot) {
      const getEndTime = () => {
        return timeslot.dateTo || moment(timeslot.startsAt).add(timeslot.length, 'minute');
      };

      return moment(timeslot.dateFrom || timeslot.startsAt).format('LT') + '-' + moment(getEndTime()).format('LT');
    },
    setAvailableDates(timeslots) {
      each(timeslots, (timeslot) => {
        timeslot.start = moment(timeslot.dateFrom.time).startOf('day').toDate();
        timeslot.end = moment(timeslot.dateTo.time).endOf('day').toDate();
      });
      this.timeslots = timeslots;
    },
    updateLoading(loading) {
      this.loading = loading;
    },
    validateForm: function() {
      this.$v.$touch();
    },
    createBooking: function () {
      this.validateForm();
      if (this.$v.$invalid || this.offerLoading) {
        return;
      }
      let sections = this.selectedOffer.actualSections.sections;
      let booking = {
        timeslotId: this.selectedTimeslot.timeslotId,
        offerKey: this.selectedOffer.offerKey,
        comment: this.form.comment,
        status: 'BOOKED',
        emailAddress: this.form.email,
        givenname: this.form.firstName,
        surname: this.form.lastName,
        phone: this.form.phone,
        selectedServiceIds: bookingSectionService.getSelectedServiceIds(sections),
        mileage: this.form.mileage
      };

      this.offerLoading = true;
      return this.$store.dispatch('offer/createBooking', booking)
        .then((res) => {
          this.$router.push({
            path: `/service-request/confirm/${res.data.key}`
          });
          return res;
        })
        .catch((err) => {
          this.requestError = true;
          return Promise.reject(new Error(err));
        })
        .finally(() => {
          this.offerLoading = false;
        })
    },
    isAdditionalServiceSelected() {
      return !!this.additionalServices.find((o) => o.selected === true)
    },
    getLocationVotings: function (locationId) {
      bookingApiService.getLocationVoting(locationId).then((res) => {
        this.comments = res.data;
        // set all comments to display overview only
        for (let i = 0; i < this.comments.length; i++) {
          this.$set(this.display, 'commentDetails-' + this.comments[i].id, false);
          this.comments[i].bookedOfferVotingTypeMap = merge(this.comments[i].bookedOfferVotingTypeMap, {
            DETAIL_RELIABILITY: ['0'],
            DETAIL_QUALITY: ['0'],
            DETAIL_FRIENDLINESS: ['0'],
            OVERALL_FEELING: ['0']
          });
          if (this.comments[i].comment) {
            this.comments[i].comment = this.comments[i].comment.trim();
          } else {
            this.comments[i].comment = null;
          }
        }
      });
    },
    gotoMessage() {
      this.$router.push({ path: '/conversation' });
    },
    gotoBooking() {
      const element = document.getElementById('booking-start');
      element.scrollIntoView(true);
    }
  },
  computed: {
    canUseCapacityAppointments() {
      return !!capacityId && contextLocationId && contextLocationId === this.selectedOffer?.location?.id;
    },
    isServiceRequest() {
      return this.$route.path.includes('service-request');
    },
    selectedServices() {
      return get(this, 'selectedOffer.actualSections.sections', []).filter(service => service.serviceType !== 'ADDITIONAL');
    },
    additionalServices() {
      return get(this, 'selectedOffer.actualSections.sections', []).filter(service => service.serviceType === 'ADDITIONAL');
    },
    ...mapGetters('offer', {
      selectedOffer: 'selectedOffer',
      offersearch: 'offersearch'
    }),
    validOffer() {
      return this.selectedOffer && !this.selectedOffer.empty;
    },
    availableTimeSlotsForDate() {
      if (this.selectedDate) {
        if (this.canUseCapacityAppointments) {
          return this.timeslots.filter(item => moment(item.start).format('YYYY-MM-DD') === moment(this.selectedDate).format('YYYY-MM-DD'))
        }

        if (this.selectedTimeslot) {
          return [this.selectedTimeslot];
        }
      }

      return [];
    }
  },
  created() {
    let offerKey = this.$route.params.key;
    if (offerKey) {
      this.loading = true;
      let promises = [];
      let agreementPromise = this.$store.dispatch('serviceCase/getAgreements');
      let bookingDetailPromise = this.$store.dispatch('offer/getOfferDetails', offerKey).then((res) => {
        this.getLocationVotings(this.selectedOffer.location.id);
        if (this.selectedOffer.expired) {
          this.$router.push({ path: '/service-request/offer-expired' });
        }
        return this.$store.dispatch('offer/getOfferSearch', res.data.offerSearchKey);
      });

      // If capacityId is set in Context, try to search timeslots there
      let timeslotPromise;
      if (this.canUseCapacityAppointments) {
        timeslotPromise = capacityApiService.listAvailableTimeslots(capacityId).then((timeslots) => {
          this.timeslots = timeslots.map((curr) => {
            return {
              start: moment(curr.startsAt).toDate(),
              startsAt: curr.startsAt,
              length: curr.length,
              end: moment(curr.startsAt).add(curr.length, 'minutes').toDate(),
              timeslotId: curr.id
            }
          })
        }).catch(() => {
          // In case of failure return default timeslots
          return bookingApiService.getTimeslots(offerKey).then((res) => {
            this.setAvailableDates(res.data);
          });
        });
      } else {
        // Else use drop-off timeslots from offer
        timeslotPromise = bookingApiService.getTimeslots(offerKey).then((res) => {
          this.setAvailableDates(res.data);
        });
      }

      promises.push(agreementPromise, bookingDetailPromise, timeslotPromise);
      Promise.all(promises)
        .then(() => {
          if (!this.form.firstName && this.$store.state.wizardData.engageCustomer?.firstName) this.form.firstName = this.$store.state.wizardData?.engageCustomer?.firstName;
          if (!this.form.lastName && this.$store.state.wizardData.engageCustomer?.lastName) this.form.lastName = this.$store.state.wizardData?.engageCustomer?.lastName;
          if (!this.form.phone && this.$store.state.wizardData.engageCustomer?.privateMobile) this.form.phone = this.$store.state.wizardData?.engageCustomer?.privateMobile;
          if (!this.form.email && this.$store.state.wizardData.engageCustomer?.email) this.form.email = this.$store.state.wizardData?.engageCustomer?.email;
          if (!this.form.mileage && this.$store.state.wizardData.engageVehicle?.mileage) this.form.mileage = this.$store.state.wizardData?.engageVehicle?.mileage;
          if (!this.form.vin && this.$store.state.wizardData.engageVehicle?.vin) this.form.vin = this.$store.state.wizardData?.engageVehicle?.vin;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  components: {
    OfferHeader,
    TimeSlot,
    DatepickerContainer,
    Accordion,
    OfferServicesReview,
    Spinner,
    WorkshopReview,
    VehicleDetails,
    WorkshopDetails,
    Customer,
    OfferServiceSelection,
    Agreements,
    BackBtn,
    PrimaryBtn
  }
};
</script>
